<template>
  <el-dialog
    :model-value="visible"
    :show-close="true"
    width="500px"
    append-to-body
    @close="handleClose"
    @update:model-value="onUpdateVisible"
  >
    <template #header>
      <div style="text-align: left;">
        <SectionTitle title="Oylik hisobot" />
      </div>
    </template>
    <div class="flex flex-col sm:flex-row gap-3 mb-[10%] modal-content">
      <div class="flex-1 field">
        <el-date-picker
          v-model="selectedMonth"
          type="month"
          placeholder="Oyni tanlang"
          :clearable="false"
          style="width: 100%"
          :first-day-of-week="1"
        />
      </div>
      <div class="flex-1 field">
        <el-select
          v-model="selectedType"
          placeholder="Turini tanlang"
          style="width: 100%"
        >
          <el-option label="Finance" value="finance" />
          <el-option label="O'quvchi" value="student" />
          <el-option label="Kurs" value="course" />
          <el-option label="O'qituvchi" value="teacher" />
          <el-option label="Davomat" value="attendance" />
        </el-select>
      </div>
    </div>
    <div class="actions">
      <el-button :loading="downloading" type="primary" @click="download">
        Yuklab olish
      </el-button>
    </div>
  </el-dialog>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, watch, onMounted } from "vue";
import apiV2 from "@/apiConfig/api-v2";
import { successAlert } from "@/utils/successAlert";
import { errorAlert } from "@/utils/errorAlert";
import SectionTitle from "./SectionTitle.vue";
import { ElMessage } from "element-plus";

const props = defineProps({
  visible: { type: Boolean, default: false },
});
const emit = defineEmits(["update:visible"]);

const selectedMonth = ref(null);
const selectedType = ref("finance");
const downloading = ref(false);

const initDefaults = () => {
  const now = new Date();
  selectedMonth.value = new Date(now.getFullYear(), now.getMonth(), 1);
  selectedType.value = "finance";
};

onMounted(() => {
  initDefaults();
});

watch(
  () => props.visible,
  (v) => {
    if (v) initDefaults();
  }
);

const onUpdateVisible = (val) => {
  emit("update:visible", val);
};

const handleClose = () => {
  emit("update:visible", false);
};

const download = async () => {
  if (!selectedMonth.value || !selectedType.value) {
    errorAlert("Ma’lumotlar to‘liq emas");
    return;
  }
  try {
    downloading.value = true;
    const year = selectedMonth.value.getFullYear();
    const month = selectedMonth.value.getMonth() + 1; // 1-12
    const type = selectedType.value;

    const response = await apiV2({
      url: "/reports/generate-monthly-statistics",
      method: "post",
      responseType: "blob",
      params: { year, month, type },
      data: {},
    });

    const contentType =
      response.headers["content-type"] || "application/octet-stream";
    const contentDisposition = response.headers["content-disposition"] || "";

    let filename = `monthly-statistics-${year}-${String(month).padStart(
      2,
      "0"
    )}-${type}`;
    const match = contentDisposition.match(
      /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
    );
    if (match) {
      filename = decodeURIComponent(match[1] || match[2]);
    } else {
      if (contentType.includes("pdf")) filename += ".pdf";
      else if (contentType.includes("sheet") || contentType.includes("excel"))
        filename += ".xlsx";
      else if (contentType.includes("csv")) filename += ".csv";
      else filename += ".bin";
    }

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    successAlert("Yuklab olindi");
    handleClose();
  } catch (e) {
    const status = e?.response?.status;
    const msg = e?.response?.data?.message || e?.message || "";
    if (status === 400 && typeof msg === "string" && /not\s*found/i.test(msg)) {
      ElMessage({ type: "info", message: "Bu oyda hisoboti mavjud emas" });
    } else {
      errorAlert("Yuklab olishda xatolik yuz berdi");
    }
  } finally {
    downloading.value = false;
  }
};
</script>

<style scoped>
.modal-content {
  min-height: 55px; /* as requested compact height */
}
.field {
  margin-bottom: 8px;
}
.actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}
</style>
